
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2, ChevronDown, GripVertical } from "lucide-react";
import { GearItem, GearCategory, categoryIcons, CAMERA_CATEGORIES, GE_CATEGORIES } from "@/types/gear";
import { formatCurrency } from "@/lib/utils";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from 'react-i18next';
import { useCurrency } from "@/contexts/CurrencyContext";
import { 
  DndContext, 
  closestCenter, 
  KeyboardSensor, 
  PointerSensor, 
  useSensor, 
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import { 
  arrayMove, 
  SortableContext, 
  sortableKeyboardCoordinates, 
  verticalListSortingStrategy 
} from '@dnd-kit/sortable';
import { SortableGearRow } from './SortableGearRow';

export interface GearTableProps {
  items: GearItem[];
  onEdit?: (item: GearItem) => void;
  onDelete?: (id: string) => void;
  onReorder?: (items: GearItem[]) => void;
  showCost?: boolean;
  canEdit?: boolean;
  categories?: GearCategory[];
  showBudget?: boolean;
  isPublicView?: boolean;
}

export function GearTable({ 
  items, 
  onEdit, 
  onDelete, 
  onReorder,
  showCost = true, 
  canEdit = true, 
  categories,
  showBudget = true,
  isPublicView = false
}: GearTableProps) {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const { currency } = useCurrency();
  
  // For public views, disable all editing capabilities
  const allowEdit = canEdit && !!(onEdit || onDelete);
  
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  // Sort items by sort_order first, then by creation order
  const sortedItems = [...items].sort((a, b) => {
    // First sort by category order
    const aCategoryIndex = [...CAMERA_CATEGORIES, ...GE_CATEGORIES].indexOf(a.category as GearCategory);
    const bCategoryIndex = [...CAMERA_CATEGORIES, ...GE_CATEGORIES].indexOf(b.category as GearCategory);
    
    if (aCategoryIndex !== bCategoryIndex) {
      return aCategoryIndex - bCategoryIndex;
    }
    
    // Then by sort_order within category, fallback to created_at
    const aOrder = a.sort_order ?? 0;
    const bOrder = b.sort_order ?? 0;
    
    if (aOrder !== bOrder) {
      return aOrder - bOrder;
    }
    
    return new Date(a.created_at || '').getTime() - new Date(b.created_at || '').getTime();
  });

  // Group items by category
  const itemsByCategory: Record<string, GearItem[]> = {};
  
  sortedItems.forEach(item => {
    if (!itemsByCategory[item.category]) {
      itemsByCategory[item.category] = [];
    }
    itemsByCategory[item.category].push(item);
  });

  // Get categories that have items
  const activeCategories = Object.keys(itemsByCategory);
  
  // Sort categories based on their order in the predefined arrays
  const orderedCategories = [...activeCategories].sort((a, b) => {
    // Get indices from respective category arrays
    const aIndexCamera = CAMERA_CATEGORIES.indexOf(a as GearCategory);
    const bIndexCamera = CAMERA_CATEGORIES.indexOf(b as GearCategory);
    const aIndexGE = GE_CATEGORIES.indexOf(a as GearCategory);
    const bIndexGE = GE_CATEGORIES.indexOf(b as GearCategory);
    
    // If both categories are in CAMERA_CATEGORIES, sort by their position there
    if (aIndexCamera >= 0 && bIndexCamera >= 0) {
      return aIndexCamera - bIndexCamera;
    }
    
    // If both categories are in GE_CATEGORIES, sort by their position there
    if (aIndexGE >= 0 && bIndexGE >= 0) {
      return aIndexGE - bIndexGE;
    }
    
    // If one is in CAMERA_CATEGORIES and the other is in GE_CATEGORIES, 
    // put CAMERA_CATEGORIES items first
    if (aIndexCamera >= 0 && bIndexGE >= 0) {
      return -1;
    }
    if (aIndexGE >= 0 && bIndexCamera >= 0) {
      return 1;
    }
    
    // Default sort by name
    return a.localeCompare(b);
  });

  const handleDragEnd = (event: DragEndEvent, category: string) => {
    const { active, over } = event;

    if (!over || active.id === over.id || !onReorder) {
      return;
    }

    const categoryItems = itemsByCategory[category];
    const oldIndex = categoryItems.findIndex(item => item.id === active.id);
    const newIndex = categoryItems.findIndex(item => item.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const reorderedCategoryItems = arrayMove(categoryItems, oldIndex, newIndex);
      
      // Update sort_order for reordered items
      const updatedCategoryItems = reorderedCategoryItems.map((item, index) => ({
        ...item,
        sort_order: index
      }));

      // Merge with other categories
      const allOtherItems = sortedItems.filter(item => item.category !== category);
      const updatedAllItems = [...allOtherItems, ...updatedCategoryItems];
      
      onReorder(updatedAllItems);
    }
  };

  if (items.length === 0) {
    return null;
  }

  const renderTableHeader = () => (
    <TableHeader className="bg-primary/10 border-b-0">
      <TableRow>
        {allowEdit && onReorder && <TableHead className="w-[40px] rounded-none text-white/90 font-medium"></TableHead>}
        <TableHead className="rounded-none text-white/90 font-medium">{t('item')}</TableHead>
        <TableHead className="w-[100px] text-right rounded-none text-white/90 font-medium">{t('qty')}</TableHead>
        {showCost && showBudget && (
          <>
            <TableHead className="w-[120px] text-right rounded-none text-white/90 font-medium">{t('rate')}</TableHead>
            <TableHead className="w-[100px] text-right rounded-none text-white/90 font-medium">{t('days')}</TableHead>
            <TableHead className="w-[120px] text-right rounded-none text-white/90 font-medium">{t('total')}</TableHead>
          </>
        )}
        <TableHead className="w-[150px] rounded-none text-white/90 font-medium">{t('vendor')}</TableHead>
        {allowEdit && <TableHead className="w-[100px] rounded-none text-white/90 font-medium">{t('actions')}</TableHead>}
      </TableRow>
    </TableHeader>
  );

  const renderCategoryTable = (category: string, categoryItems: GearItem[]) => {
    return (
      <div key={category} className="mb-6">
        <h3 className="text-lg font-semibold mb-2 text-white/90">{category}</h3>
        <div className="bg-card/10 border border-white/5 rounded-lg overflow-hidden">
          <Table>
            {renderTableHeader()}
            <DndContext 
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={(event) => handleDragEnd(event, category)}
            >
              <SortableContext 
                items={categoryItems.map(item => item.id)}
                strategy={verticalListSortingStrategy}
              >
                <TableBody>
                  {categoryItems.map((item, index) => (
                    <SortableGearRow
                      key={item.id}
                      item={item}
                      index={index}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      showCost={showCost}
                      showBudget={showBudget}
                      allowEdit={allowEdit}
                      canReorder={!!(onReorder && allowEdit)}
                    />
                  ))}
                </TableBody>
              </SortableContext>
            </DndContext>
          </Table>
        </div>
      </div>
    );
  };

  const renderCategoryAccordion = (category: string, categoryItems: GearItem[]) => {
    return (
      <div key={category} className="mb-4">
        <h3 className="text-lg font-semibold mb-2 text-white/90">{category}</h3>
        <div className="space-y-2">
          <Accordion type="single" collapsible className="w-full space-y-2">
            {categoryItems.map((item) => {
              const total = (item.quantity || 0) * (item.cost_per_unit || 0) * (item.days_needed || 1);
              const getItemClass = () => {
                if (!item.color) return "border rounded-lg overflow-hidden bg-card/50 backdrop-blur-sm";
                switch (item.color) {
                  case 'blue': return 'border rounded-lg overflow-hidden bg-blue-500/20 border-blue-500/30 backdrop-blur-sm';
                  case 'green': return 'border rounded-lg overflow-hidden bg-green-500/20 border-green-500/30 backdrop-blur-sm';
                  case 'yellow': return 'border rounded-lg overflow-hidden bg-yellow-500/20 border-yellow-500/30 backdrop-blur-sm';
                  case 'red': return 'border rounded-lg overflow-hidden bg-red-500/20 border-red-500/30 backdrop-blur-sm';
                  case 'purple': return 'border rounded-lg overflow-hidden bg-purple-500/20 border-purple-500/30 backdrop-blur-sm';
                  default: return "border rounded-lg overflow-hidden bg-card/50 backdrop-blur-sm";
                }
              };
              return (
                <AccordionItem 
                  key={item.id} 
                  value={item.id}
                  className={getItemClass()}
                >
                  <AccordionTrigger className="px-4 py-2 hover:no-underline">
                    <div className="flex items-center justify-between w-full">
                      <span className="text-left">{item.name}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">
                          {t('qty_label', { qty: item.quantity || 0 })}
                        </span>
                        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-3 pt-1 space-y-2">
                    {item.notes && (
                      <p className="text-sm text-muted-foreground">{item.notes}</p>
                    )}
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {showCost && showBudget && (
                        <>
                          <div>
                            <span className="text-muted-foreground">{t('rate_label')}</span>
                            <span>{item.cost_per_unit ? formatCurrency(item.cost_per_unit, currency) : '-'}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">{t('days_label')}</span>
                            <span>{item.days_needed || 1}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">{t('total_label')}</span>
                            <span>{formatCurrency(total, currency)}</span>
                          </div>
                        </>
                      )}
                      <div>
                        <span className="text-muted-foreground">{t('vendor_label')}</span>
                        <span>{item.vendors?.name || '-'}</span>
                      </div>
                    </div>
                    {allowEdit && (
                      <div className="flex justify-end gap-2 mt-2">
                        {onEdit && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onEdit(item)}
                          >
                            <Pencil className="h-4 w-4 mr-1" />
                            {t('edit')}
                          </Button>
                        )}
                        {onDelete && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => onDelete(item.id)}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            {t('delete')}
                          </Button>
                        )}
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      </div>
    );
  };

  if (isMobile) {
    return (
      <div className="space-y-4">
        {orderedCategories.map((category) => 
          renderCategoryAccordion(category, itemsByCategory[category])
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {orderedCategories.map((category) => 
        renderCategoryTable(category, itemsByCategory[category])
      )}
    </div>
  );
}
